import { useEffect, useMemo, useState } from "react";
import { api, getToken, setToken, clearToken, resolveImage } from "@/lib/api";
import ArkHeader from "@/components/ArkHeader";
import ImageCropper from "@/components/ImageCropper";
import { CATEGORIES, categoryName } from "@/lib/categories";
import { toast } from "sonner";
import { Trash2, Pencil, Upload, Crop, ArrowUp, ArrowDown, Star, Mail, Sparkles, Eye, BarChart3, Download, Database, Bookmark, Tag, Images } from "lucide-react";

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      setToken(data.token);
      window.dispatchEvent(new Event("ark-auth-change"));
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm border border-[var(--ark-border)] p-10 bg-white"
      >
        <div className="font-display font-bold text-3xl tracking-tight">ARK</div>
        <p className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mt-1">
          Admin workshop
        </p>
        <div className="mt-8">
          <label className="ark-label" htmlFor="pw">
            Password
          </label>
          <input
            id="pw"
            data-testid="admin-password"
            type="password"
            className="ark-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="ark-btn w-full mt-10"
        >
          {loading ? "Entering…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function Pieces() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null); // data URL being cropped
  const [recropIdx, setRecropIdx] = useState(null); // index being re-cropped (null = new image)
  const [filterCat, setFilterCat] = useState("all"); // 'all' | category slug | 'uncategorised'
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    dimensions: "",
    price_estimate: "",
    base_price: "",
    stain_price: "",
    paint_price: "",
    images: [],
    is_sold: false,
  });

  const load = () =>
    api.get("/furniture").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  // Counts per category for the chip row
  const catCounts = useMemo(() => {
    const m = { all: items.length, uncategorised: 0 };
    for (const it of items) {
      const c = it.category || "uncategorised";
      m[c] = (m[c] || 0) + 1;
    }
    return m;
  }, [items]);

  const visibleCats = useMemo(
    () => CATEGORIES.filter((c) => (catCounts[c.slug] || 0) > 0),
    [catCounts],
  );

  const filteredItems = useMemo(() => {
    if (filterCat === "all") return items;
    if (filterCat === "uncategorised")
      return items.filter((it) => !it.category);
    return items.filter((it) => it.category === filterCat);
  }, [items, filterCat]);

  const reset = () => {
    setEditing(null);
    setForm({
      title: "",
      description: "",
      category: "",
      dimensions: "",
      price_estimate: "",
      base_price: "",
      stain_price: "",
      paint_price: "",
      images: [],
      is_sold: false,
    });
  };

  // Open cropper on a newly picked file
  const openCropperFromFile = (file) => {
    if (!file) return;
    setRecropIdx(null);
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  // Open cropper on an existing image (re-crop by index)
  const openCropperFromUrl = async (idx) => {
    const url = form.images[idx];
    if (!url) return;
    try {
      const resp = await fetch(resolveImage(url), { mode: "cors" });
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onload = () => {
        setRecropIdx(idx);
        setCropSrc(reader.result);
      };
      reader.readAsDataURL(blob);
    } catch {
      toast.error("Couldn't load image for re-cropping");
    }
  };

  // Upload a File/Blob, then either append to images (new) or replace at recropIdx
  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => {
        const next = [...f.images];
        if (recropIdx != null) next[recropIdx] = data.url;
        else next.push(data.url);
        return { ...f, images: next };
      });
      toast.success(recropIdx != null ? "Photo updated" : "Photo added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setRecropIdx(null);
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };
  const makePrimary = (idx) => {
    setForm((f) => {
      const arr = [...f.images];
      const [img] = arr.splice(idx, 1);
      arr.unshift(img);
      return { ...f, images: arr };
    });
  };
  const move = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.images];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, images: arr };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || form.images.length === 0) {
      toast.error("Title, description and at least one photo are required");
      return;
    }
    const toPrice = (v) => {
      const s = String(v ?? "").trim();
      if (!s) return null;
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      title: form.title,
      description: form.description,
      category: form.category || null,
      dimensions: form.dimensions,
      price_estimate: form.price_estimate,
      base_price: toPrice(form.base_price),
      stain_price: toPrice(form.stain_price),
      paint_price: toPrice(form.paint_price),
      images: form.images,
      image_url: form.images[0],
      is_sold: !!form.is_sold,
    };
    try {
      if (editing) {
        await api.patch(`/admin/furniture/${editing}`, payload);
        toast.success("Piece updated");
      } else {
        await api.post("/admin/furniture", payload);
        toast.success("Piece added");
      }
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (item) => {
    setEditing(item.id);
    const imgs =
      item.images && item.images.length > 0
        ? item.images
        : item.image_url
          ? [item.image_url]
          : [];
    setForm({
      title: item.title || "",
      description: item.description || "",
      category: item.category || "",
      dimensions: item.dimensions || "",
      price_estimate: item.price_estimate || "",
      base_price: item.base_price != null ? String(item.base_price) : "",
      stain_price: item.stain_price != null ? String(item.stain_price) : "",
      paint_price: item.paint_price != null ? String(item.paint_price) : "",
      images: imgs,
      is_sold: !!item.is_sold,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this piece?")) return;
    try {
      await api.delete(`/admin/furniture/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleSold = async (item) => {
    try {
      await api.patch(`/admin/furniture/${item.id}`, { is_sold: !item.is_sold });
      toast.success(item.is_sold ? "Marked available" : "Marked sold");
      load();
    } catch {
      toast.error("Couldn't update status");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          onCancel={() => {
            setCropSrc(null);
            setRecropIdx(null);
          }}
          onCropped={async (file) => {
            setCropSrc(null);
            await uploadFile(file);
          }}
        />
      )}
      <form
        data-testid="piece-form"
        onSubmit={save}
        className="lg:col-span-5 border border-[var(--ark-border)] p-8 bg-white h-fit"
      >
        <h3 className="font-display text-2xl">
          {editing ? "Edit piece" : "Add a new piece"}
        </h3>
        <div className="mt-6 space-y-5">
          <div>
            <label className="ark-label">Title</label>
            <input
              data-testid="piece-title-input"
              className="ark-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Heritage Dining Table"
            />
          </div>
          <div>
            <label className="ark-label">Description</label>
            <textarea
              data-testid="piece-desc-input"
              className="ark-input"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Story, materials, construction notes…"
            />
          </div>
          <div>
            <label className="ark-label">Category</label>
            <select
              data-testid="piece-category-input"
              className="ark-input"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              <option value="">— Select a category —</option>
              {CATEGORIES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ark-label">Dimensions</label>
            <input
              data-testid="piece-dim-input"
              className="ark-input"
              value={form.dimensions}
              onChange={(e) =>
                setForm({ ...form, dimensions: e.target.value })
              }
              placeholder='72" × 38" × 30"'
            />
          </div>
          <div className="border border-[var(--ark-border)] p-4 space-y-4 bg-[var(--ark-bone)]">
            <div className="text-[0.62rem] uppercase tracking-[0.18em] text-[var(--ark-accent)]">
              Pricing (CAD, before HST)
            </div>
            <div>
              <label className="ark-label">Base Price · shown on cards & before finish picked</label>
              <input
                type="number"
                step="1"
                min="0"
                data-testid="piece-base-price-input"
                className="ark-input"
                value={form.base_price}
                onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                placeholder="e.g. 1850"
              />
            </div>
            <div>
              <label className="ark-label">Stain Price · restoration</label>
              <input
                type="number"
                step="1"
                min="0"
                data-testid="piece-stain-price-input"
                className="ark-input"
                value={form.stain_price}
                onChange={(e) => setForm({ ...form, stain_price: e.target.value })}
                placeholder="e.g. 2100"
              />
            </div>
            <div>
              <label className="ark-label">Paint Price · premium refinishing</label>
              <input
                type="number"
                step="1"
                min="0"
                data-testid="piece-paint-price-input"
                className="ark-input"
                value={form.paint_price}
                onChange={(e) => setForm({ ...form, paint_price: e.target.value })}
                placeholder="e.g. 2400"
              />
            </div>
            <p className="text-[0.65rem] uppercase tracking-[0.16em] text-[var(--ark-muted)]">
              Customer sees Base Price first; Stain or Paint price replaces it once they pick that finish.
            </p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="piece-sold-input"
              checked={!!form.is_sold}
              onChange={(e) =>
                setForm({ ...form, is_sold: e.target.checked })
              }
              className="h-4 w-4 accent-[var(--ark-accent)] cursor-pointer"
            />
            <span className="text-sm">
              Mark this piece as <strong>sold</strong> (still visible to customers, no longer requestable)
            </span>
          </label>
          <div>
            <label className="ark-label">
              Photos ({form.images.length}) — first is the cover
            </label>
            <div className="grid grid-cols-3 gap-3" data-testid="piece-images">
              {form.images.map((url, i) => (
                <div
                  key={`${url}-${i}`}
                  data-testid={`piece-image-${i}`}
                  className={`relative aspect-[4/5] border overflow-hidden group ${
                    i === 0
                      ? "border-[var(--ark-accent)]"
                      : "border-[var(--ark-border)]"
                  }`}
                >
                  <img
                    src={resolveImage(url)}
                    alt={`piece ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                  {i === 0 && (
                    <span className="absolute top-1 left-1 bg-[var(--ark-accent)] text-white text-[0.55rem] uppercase tracking-[0.14em] px-1.5 py-0.5 inline-flex items-center gap-1">
                      <Star size={10} /> Cover
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-black/70 flex items-center justify-between px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title="Move left"
                        onClick={() => move(i, -1)}
                        data-testid={`piece-image-${i}-up`}
                        className="text-white/80 hover:text-white disabled:opacity-30"
                        disabled={i === 0}
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        type="button"
                        title="Move right"
                        onClick={() => move(i, 1)}
                        data-testid={`piece-image-${i}-down`}
                        className="text-white/80 hover:text-white disabled:opacity-30"
                        disabled={i === form.images.length - 1}
                      >
                        <ArrowDown size={12} />
                      </button>
                      {i !== 0 && (
                        <button
                          type="button"
                          title="Make cover"
                          onClick={() => makePrimary(i)}
                          data-testid={`piece-image-${i}-primary`}
                          className="text-white/80 hover:text-[var(--ark-accent)]"
                        >
                          <Star size={12} />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title="Adjust crop"
                        onClick={() => openCropperFromUrl(i)}
                        data-testid={`piece-image-${i}-recrop`}
                        className="text-white/80 hover:text-[var(--ark-accent)]"
                      >
                        <Crop size={12} />
                      </button>
                      <button
                        type="button"
                        title="Remove"
                        onClick={() => removeImage(i)}
                        data-testid={`piece-image-${i}-remove`}
                        className="text-white/80 hover:text-red-400"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <label
                data-testid="piece-upload-label"
                className="aspect-[4/5] border border-dashed border-[var(--ark-border)] flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[var(--ark-accent)] hover:text-[var(--ark-accent)] transition-colors text-[var(--ark-muted)] text-xs uppercase tracking-[0.14em]"
              >
                <Upload size={16} />
                {uploading ? "Uploading…" : "Add photo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="piece-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) openCropperFromFile(f);
                  }}
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-[var(--ark-muted)]">
              Each photo is cropped to 4:5 portrait. Hover a photo to reorder,
              adjust, or remove it.
            </p>
          </div>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <button
            type="submit"
            data-testid="piece-save-btn"
            className="ark-btn"
          >
            {editing ? "Save changes" : "Add piece"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={reset}
              className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-black"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="lg:col-span-7">
        <div className="flex items-end justify-between gap-4 mb-5">
          <h3 className="font-display text-2xl">
            Collection ({filteredItems.length}
            {filterCat !== "all" ? ` of ${items.length}` : ""})
          </h3>
        </div>
        {/* Category filter chips */}
        <div
          data-testid="admin-category-filter"
          className="flex items-center gap-2 flex-wrap mb-5"
        >
          {[
            { slug: "all", name: "All" },
            ...visibleCats,
            ...(catCounts.uncategorised > 0
              ? [{ slug: "uncategorised", name: "Uncategorised" }]
              : []),
          ].map((c) => {
            const active = filterCat === c.slug;
            const count = catCounts[c.slug] || 0;
            return (
              <button
                key={c.slug}
                type="button"
                data-testid={`filter-cat-${c.slug}`}
                onClick={() => setFilterCat(c.slug)}
                className={`text-[0.62rem] uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors ${
                  active
                    ? "bg-black text-white border-black"
                    : "bg-white text-[var(--ark-muted)] border-[var(--ark-border)] hover:border-black hover:text-black"
                }`}
              >
                {c.name}
                <span
                  className={`ml-2 ${active ? "text-white/70" : "text-[var(--ark-muted)]"}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              data-testid={`admin-piece-${item.id}`}
              className="flex items-center gap-4 border border-[var(--ark-border)] p-4 bg-white"
            >
              <div className="relative h-20 w-20 flex-shrink-0">
                <img
                  src={resolveImage(item.image_url)}
                  alt={item.title}
                  className={`h-20 w-20 object-cover ${item.is_sold ? "opacity-60" : ""}`}
                />
                {item.is_sold && (
                  <span className="absolute top-1 left-1 bg-black text-white text-[0.5rem] uppercase tracking-[0.18em] px-1.5 py-0.5">
                    Sold
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {item.code && (
                    <span
                      data-testid={`piece-code-${item.id}`}
                      className="font-mono text-[0.6rem] tracking-[0.18em] text-white bg-black px-2 py-0.5"
                    >
                      {item.code}
                    </span>
                  )}
                  <div className="font-display text-lg truncate">
                    {item.title}
                  </div>
                </div>
                <div className="text-xs text-[var(--ark-muted)] truncate mt-1">
                  {[
                    item.category && categoryName(item.category),
                    item.dimensions,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <button
                onClick={() => toggleSold(item)}
                data-testid={`piece-toggle-sold-${item.id}`}
                className={`text-[0.62rem] uppercase tracking-[0.18em] px-2.5 py-1.5 border transition-colors ${
                  item.is_sold
                    ? "border-black bg-black text-white hover:bg-white hover:text-black"
                    : "border-[var(--ark-border)] text-[var(--ark-muted)] hover:border-black hover:text-black"
                }`}
                title={item.is_sold ? "Mark available" : "Mark sold"}
              >
                {item.is_sold ? "Sold" : "Mark sold"}
              </button>
              <button
                onClick={() => edit(item)}
                data-testid={`piece-edit-${item.id}`}
                className="p-2 hover:text-[var(--ark-accent)]"
                title="Edit"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => remove(item.id)}
                data-testid={`piece-delete-${item.id}`}
                className="p-2 hover:text-red-700"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {filteredItems.length === 0 && (
            <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-6 text-center">
              {items.length === 0
                ? "No pieces yet."
                : "No pieces in this category yet."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Palettes() {
  const [palette, setPalette] = useState({
    stains: [],
    paints: [],
    hardware: [],
    sheens: ["Satin", "Semi-Gloss"],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/palette").then((r) =>
      setPalette({
        stains: r.data.stains || [],
        paints: r.data.paints || [],
        hardware: r.data.hardware || [],
        sheens: r.data.sheens || ["Satin", "Semi-Gloss"],
      }),
    );
  }, []);

  const update = (type, i, key, v) => {
    const next = { ...palette, [type]: [...palette[type]] };
    next[type][i] = { ...next[type][i], [key]: v };
    setPalette(next);
  };
  const addColor = (type) =>
    setPalette({
      ...palette,
      [type]: [...palette[type], { name: "", hex: "#c19a6b" }],
    });
  const addHardware = () =>
    setPalette({
      ...palette,
      hardware: [...palette.hardware, { name: "", description: "" }],
    });
  const remove = (type, i) => {
    const next = { ...palette, [type]: palette[type].filter((_, j) => j !== i) };
    setPalette(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/palette", palette);
      toast.success("Palette saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const renderColorList = (type, label) => (
    <div className="border border-[var(--ark-border)] p-6 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display text-xl">{label}</h4>
        <button
          onClick={() => addColor(type)}
          data-testid={`add-${type}`}
          className="text-[0.7rem] uppercase tracking-[0.18em] hover:text-[var(--ark-accent)]"
        >
          + Add
        </button>
      </div>
      <div className="space-y-3">
        {palette[type].map((s, i) => (
          <div key={`${type}-${i}-${s.name || ""}`} className="flex items-center gap-3">
            <input
              type="color"
              value={s.hex}
              data-testid={`${type}-hex-${i}`}
              onChange={(e) => update(type, i, "hex", e.target.value)}
              className="h-10 w-12 border border-[var(--ark-border)] bg-white cursor-pointer"
            />
            <input
              type="text"
              value={s.name}
              data-testid={`${type}-name-${i}`}
              onChange={(e) => update(type, i, "name", e.target.value)}
              placeholder="Finish name"
              className="ark-input !border-b"
            />
            <button
              onClick={() => remove(type, i)}
              data-testid={`${type}-remove-${i}`}
              className="p-2 hover:text-red-700"
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderHardware = () => (
    <div className="border border-[var(--ark-border)] p-6 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display text-xl">Hardware Options</h4>
        <button
          onClick={addHardware}
          data-testid="add-hardware"
          className="text-[0.7rem] uppercase tracking-[0.18em] hover:text-[var(--ark-accent)]"
        >
          + Add
        </button>
      </div>
      <div className="space-y-3">
        {palette.hardware.map((h, i) => (
          <div key={`hw-${i}-${h.name || ""}`} className="flex items-center gap-3">
            <input
              type="text"
              value={h.name}
              data-testid={`hardware-name-${i}`}
              onChange={(e) => update("hardware", i, "name", e.target.value)}
              placeholder="e.g. Brushed Brass"
              className="ark-input !border-b flex-1"
            />
            <input
              type="text"
              value={h.description || ""}
              data-testid={`hardware-desc-${i}`}
              onChange={(e) =>
                update("hardware", i, "description", e.target.value)
              }
              placeholder="Short description"
              className="ark-input !border-b flex-1"
            />
            <button
              onClick={() => remove("hardware", i)}
              data-testid={`hardware-remove-${i}`}
              className="p-2 hover:text-red-700"
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <p className="text-sm text-[var(--ark-muted)] mb-6">
        These finishes appear on every piece in the collection.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderColorList("stains", "Stain Finishes")}
        {renderColorList("paints", "Paint Finishes")}
      </div>
      <div className="mt-6">{renderHardware()}</div>
      <div className="mt-6 border border-[var(--ark-border)] p-6 bg-white">
        <h4 className="font-display text-xl mb-2">Sheens</h4>
        <p className="text-xs text-[var(--ark-muted)] mb-3">
          Fixed set. Customers choose one when requesting a piece.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {palette.sheens.map((s) => (
            <div
              key={s}
              className="px-4 py-2 border border-[var(--ark-border)] text-sm"
            >
              {s}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8">
        <button
          onClick={save}
          disabled={saving}
          data-testid="palette-save-btn"
          className="ark-btn"
        >
          {saving ? "Saving…" : "Save palette"}
        </button>
      </div>
    </div>
  );
}

function Quotes() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/quotes").then((r) => setItems(r.data));
  }, []);
  return (
    <div data-testid="quotes-list">
      {items.length === 0 ? (
        <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-8 text-center">
          No quote requests yet.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((q) => (
            <div
              key={q.id}
              data-testid={`quote-${q.id}`}
              className="border border-[var(--ark-border)] p-6 bg-white"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {q.furniture_code && (
                      <span
                        data-testid={`quote-code-${q.id}`}
                        className="font-mono text-[0.6rem] tracking-[0.18em] text-white bg-black px-2 py-0.5"
                      >
                        {q.furniture_code}
                      </span>
                    )}
                    <div className="font-display text-xl">{q.furniture_title}</div>
                  </div>
                  <div className="text-xs text-[var(--ark-muted)] mt-1">
                    {new Date(q.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span
                    className="h-5 w-5 rounded-full border border-[var(--ark-border)]"
                    style={{ background: q.finish_hex }}
                  />
                  <span className="uppercase tracking-[0.14em]">
                    {q.finish_type} · {q.finish_name}
                  </span>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="ark-label">Customer</div>
                  <div>{q.customer_name}</div>
                </div>
                <div>
                  <div className="ark-label">Email</div>
                  <div>
                    <a
                      href={`mailto:${q.customer_email}`}
                      className="hover:text-[var(--ark-accent)]"
                    >
                      {q.customer_email}
                    </a>
                  </div>
                </div>
                <div>
                  <div className="ark-label">Phone</div>
                  <div>{q.customer_phone || "—"}</div>
                </div>
                <div>
                  <div className="ark-label">Quoted price · Hardware</div>
                  <div>
                    {q.final_price != null
                      ? `$${Number(q.final_price).toLocaleString("en-US", { maximumFractionDigits: 0 })} + HST`
                      : "—"}{" "}
                    · {q.hardware || "—"}
                  </div>
                </div>
              </div>
              {q.message && (
                <div className="mt-4">
                  <div className="ark-label">Notes</div>
                  <div className="text-sm text-[var(--ark-muted)]">
                    {q.message}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Subscribers() {
  const [items, setItems] = useState([]);
  const load = () =>
    api.get("/admin/subscribers").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Couldn't remove");
    }
  };
  return (
    <div data-testid="subscribers-list">
      {items.length === 0 ? (
        <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-8 text-center">
          No one has signed up yet.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mb-2">
            {items.length} subscriber{items.length === 1 ? "" : "s"} — emailed automatically when you upload a piece in their categories
          </div>
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`sub-${s.id}`}
              className="flex items-start justify-between gap-4 border border-[var(--ark-border)] p-4 bg-white"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-[var(--ark-muted)]" />
                  <a
                    href={`mailto:${s.email}`}
                    className="hover:text-[var(--ark-accent)] truncate"
                  >
                    {s.email}
                  </a>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {s.categories.map((slug) => (
                    <span
                      key={slug}
                      className="text-[0.6rem] uppercase tracking-[0.14em] border border-[var(--ark-border)] px-2 py-0.5 text-[var(--ark-muted)]"
                    >
                      {categoryName(slug)}
                    </span>
                  ))}
                </div>
                <div className="text-[0.62rem] uppercase tracking-[0.14em] text-[var(--ark-muted)] mt-2">
                  Joined {new Date(s.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => remove(s.id)}
                data-testid={`sub-remove-${s.id}`}
                className="p-2 hover:text-red-700"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sourcing() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/sourcing").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Remove this request?")) return;
    try {
      await api.delete(`/admin/sourcing/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Couldn't remove");
    }
  };
  return (
    <div data-testid="sourcing-list">
      {items.length === 0 ? (
        <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-8 text-center">
          No sourcing requests yet.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`sourcing-${r.id}`}
              className="border border-[var(--ark-border)] p-6 bg-white"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[0.62rem] uppercase tracking-[0.18em] text-[var(--ark-accent)]">
                    {r.category ? categoryName(r.category) : "Any type"}
                  </div>
                  <div className="font-display text-xl mt-1">
                    {r.customer_name}
                  </div>
                  <div className="text-xs text-[var(--ark-muted)] mt-1">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => remove(r.id)}
                  data-testid={`sourcing-remove-${r.id}`}
                  className="p-2 hover:text-red-700"
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">
                {r.description}
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="ark-label">Email</div>
                  <a
                    href={`mailto:${r.customer_email}`}
                    className="hover:text-[var(--ark-accent)]"
                  >
                    {r.customer_email}
                  </a>
                </div>
                <div>
                  <div className="ark-label">Phone</div>
                  <div>{r.customer_phone || "—"}</div>
                </div>
                <div>
                  <div className="ark-label">Budget</div>
                  <div>{r.budget || "—"}</div>
                </div>
                <div>
                  <div className="ark-label">Timing</div>
                  <div>{r.timing || "—"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitorStrip() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/visitor-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const Cell = ({ label, value, sub, accent }) => (
    <div className="flex-1 min-w-[110px]">
      <div className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-muted)]">
        {label}
      </div>
      <div
        className={`font-display text-2xl mt-0.5 ${accent ? "text-[var(--ark-accent)]" : ""}`}
      >
        {value}
      </div>
      {sub != null && (
        <div className="text-[0.62rem] uppercase tracking-[0.14em] text-[var(--ark-muted)] mt-0.5">
          {sub}
        </div>
      )}
    </div>
  );
  return (
    <div
      data-testid="visitor-strip"
      className="border border-[var(--ark-border)] bg-white px-5 py-4 mb-4"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-accent)] mr-4">
          <Eye size={11} /> Site visitors
        </div>
        <Cell
          label="Today"
          value={stats.unique_today}
          sub={`${stats.views_today} views`}
          accent
        />
        <Cell
          label="Last 7 days"
          value={stats.unique_week}
          sub={`${stats.views_week} views`}
        />
        <Cell
          label="Last 30 days"
          value={stats.unique_month}
          sub={`${stats.views_month} views`}
        />
        <Cell
          label="Last 365 days"
          value={stats.unique_year}
          sub={`${stats.views_year} views`}
        />
        <Cell
          label="All time"
          value={stats.unique_total}
          sub={`${stats.views_total} views`}
        />
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--ark-border)] text-[0.62rem] uppercase tracking-[0.14em] text-[var(--ark-muted)]">
        People = unique visitors. Visits while you&apos;re logged in as admin aren&apos;t counted.
      </div>
    </div>
  );
}

function EngagementStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/stats/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;

  const fmtDur = (s) => {
    if (!s || s <= 0) return "—";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <div
      data-testid="engagement-strip"
      className="border border-[var(--ark-border)] bg-white px-5 py-5 mb-8"
    >
      <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-accent)] mb-4">
        <BarChart3 size={11} /> Engagement
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Top pieces */}
        <div className="md:col-span-5">
          <div className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mb-3">
            Top viewed pieces
          </div>
          {data.top_pieces.length === 0 ? (
            <div className="text-xs text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-4">
              No piece views tracked yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="top-pieces">
              {data.top_pieces.map((p, i) => (
                <div
                  key={p.piece_id}
                  data-testid={`top-piece-${i}`}
                  className="flex items-center gap-3"
                >
                  <span className="text-[0.62rem] tracking-[0.18em] text-[var(--ark-muted)] font-mono w-5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {p.image_url && (
                    <img
                      src={resolveImage(p.image_url)}
                      alt=""
                      className="h-9 w-9 object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      {p.code && (
                        <span className="font-mono text-[0.58rem] tracking-[0.16em] text-white bg-black px-1.5 py-0.5 mr-2 align-middle">
                          {p.code}
                        </span>
                      )}
                      {p.title || p.piece_id}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-base leading-none">
                      {p.views}
                    </div>
                    <div className="text-[0.55rem] uppercase tracking-[0.14em] text-[var(--ark-muted)] mt-0.5">
                      {p.unique_visitors} ppl
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Funnel */}
        <div className="md:col-span-5">
          <div className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mb-3">
            How far visitors get
          </div>
          <div className="space-y-2.5" data-testid="funnel">
            {data.funnel.map((f) => (
              <div key={f.key} data-testid={`funnel-${f.key}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-black">{f.label}</span>
                  <span className="font-mono text-[var(--ark-muted)]">
                    {f.visitors} · {f.pct_of_first}%
                  </span>
                </div>
                <div className="h-1.5 bg-[var(--ark-bone)] border border-[var(--ark-border)]">
                  <div
                    className="h-full bg-[var(--ark-accent)]"
                    style={{ width: `${Math.min(100, f.pct_of_first)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Time on site */}
        <div className="md:col-span-2">
          <div className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mb-3">
            Avg time on site
          </div>
          <div className="space-y-3" data-testid="time-on-site">
            <div>
              <div className="font-display text-2xl text-[var(--ark-accent)]">
                {fmtDur(data.avg_session_seconds_today)}
              </div>
              <div className="text-[0.6rem] uppercase tracking-[0.16em] text-[var(--ark-muted)] mt-0.5">
                Today
              </div>
            </div>
            <div>
              <div className="font-display text-2xl">
                {fmtDur(data.avg_session_seconds_total)}
              </div>
              <div className="text-[0.6rem] uppercase tracking-[0.16em] text-[var(--ark-muted)] mt-0.5">
                All time
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AIUsageStrip() {
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftRate, setDraftRate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/ai-usage").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  if (!stats) return null;

  const fmtMoney = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: v < 1 ? 2 : 2,
      maximumFractionDigits: v < 1 ? 3 : 2,
    }).format(v);

  const Cell = ({ label, count, cost, accent }) => (
    <div className="flex-1 min-w-[120px]">
      <div className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-muted)]">
        {label}
      </div>
      <div className={`font-display text-2xl mt-0.5 ${accent ? "text-[var(--ark-accent)]" : ""}`}>
        {count}
      </div>
      <div className="text-[0.62rem] uppercase tracking-[0.14em] text-[var(--ark-muted)] mt-0.5">
        ≈ {fmtMoney(cost)}
      </div>
    </div>
  );

  const saveRate = async () => {
    const n = parseFloat(draftRate);
    if (Number.isNaN(n) || n < 0 || n > 5) {
      toast.error("Rate must be between 0 and 5");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ai-rate", { cost_per_image: n });
      setStats(data);
      setEditing(false);
      toast.success("Rate updated");
    } catch {
      toast.error("Couldn't update rate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="ai-usage-strip"
      className="border border-[var(--ark-border)] bg-[var(--ark-bone)] px-5 py-4 mb-8"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-accent)] mr-4">
          <Sparkles size={11} /> AI previews
        </div>
        <Cell label="Today" count={stats.today} cost={stats.cost_today} accent />
        <Cell label="Last 7 days" count={stats.week} cost={stats.cost_week} />
        <Cell label="Last 30 days" count={stats.month} cost={stats.cost_month} />
        <Cell label="All time" count={stats.total} cost={stats.cost_total} />
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--ark-border)] flex items-center gap-3 flex-wrap text-[0.65rem] uppercase tracking-[0.14em] text-[var(--ark-muted)]">
        Estimated rate:
        {editing ? (
          <>
            <span className="text-[var(--ark-muted)]">USD $</span>
            <input
              type="number"
              step="0.001"
              min="0"
              max="5"
              value={draftRate}
              data-testid="ai-rate-input"
              onChange={(e) => setDraftRate(e.target.value)}
              className="w-24 border border-[var(--ark-border)] bg-white px-2 py-1 text-xs"
              autoFocus
            />
            <span>per image</span>
            <button
              type="button"
              onClick={saveRate}
              disabled={saving}
              data-testid="ai-rate-save"
              className="text-[var(--ark-accent)] hover:text-black disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="hover:text-black"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-black normal-case tracking-normal">
              {fmtMoney(stats.cost_per_image)}
            </span>
            <span>per image</span>
            <button
              type="button"
              data-testid="ai-rate-edit"
              onClick={() => {
                setDraftRate(String(stats.cost_per_image));
                setEditing(true);
              }}
              className="hover:text-[var(--ark-accent)] underline-offset-2 hover:underline"
            >
              Edit rate
            </button>
            <span className="ml-auto text-[0.6rem] tracking-[0.16em]">
              Cached previews are free
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("pieces");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/admin/verify")
      .then(() => setAuthed(true))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  // Allow other components (e.g. BackupReminder) to switch the tab
  useEffect(() => {
    const onTab = (e) => {
      if (e?.detail) setTab(e.detail);
    };
    window.addEventListener("ark-tab", onTab);
    return () => window.removeEventListener("ark-tab", onTab);
  }, []);

  const logout = () => {
    clearToken();
    window.dispatchEvent(new Event("ark-auth-change"));
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="bg-white min-h-screen">
        <ArkHeader />
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-white min-h-screen">
      <ArkHeader />
      {!authed ? (
        <Login onLogin={() => setAuthed(true)} />
      ) : (
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]">
                Workshop
              </span>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
                Admin Dashboard
              </h1>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-black"
            >
              Log out
            </button>
          </div>
          <VisitorStrip />
          <BackupReminder />
          <EngagementStrip />
          <AIUsageStrip />
          <div className="flex items-center gap-6 border-b border-[var(--ark-border)] mb-10 overflow-x-auto">
            {[
              { k: "pieces", l: "Pieces" },
              { k: "palette", l: "Palette" },
              { k: "transformations", l: "Transformations" },
              { k: "quotes", l: "Quote Requests" },
              { k: "saved", l: "Saved Designs" },
              { k: "coupons", l: "Coupons" },
              { k: "subscribers", l: "Subscribers" },
              { k: "backup", l: "Backup & Restore" },
            ].map((t) => (
              <button
                key={t.k}
                data-testid={`admin-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`pb-3 text-[0.72rem] uppercase tracking-[0.18em] border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.k
                    ? "border-[var(--ark-accent)] text-black"
                    : "border-transparent text-[var(--ark-muted)] hover:text-black"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
          {tab === "pieces" && <Pieces />}
          {tab === "palette" && <Palettes />}
          {tab === "transformations" && <Transformations />}
          {tab === "quotes" && <Quotes />}
          {tab === "saved" && <SavedDesigns />}
          {tab === "coupons" && <Coupons />}
          {tab === "subscribers" && <Subscribers />}
          {tab === "backup" && <Backup />}
        </div>
      )}
    </div>
  );
}

function BackupReminder() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/admin/backup-status").then((r) => setStatus(r.data)).catch(() => {});
    const onSaved = () =>
      api.get("/admin/backup-status").then((r) => setStatus(r.data)).catch(() => {});
    window.addEventListener("ark-backup-saved", onSaved);
    return () => window.removeEventListener("ark-backup-saved", onSaved);
  }, []);
  if (!status || !status.overdue) return null;
  const msg = status.last_at
    ? `Backup due — last saved ${status.days_since} day${status.days_since === 1 ? "" : "s"} ago`
    : "You haven't downloaded a backup yet";
  return (
    <div
      data-testid="backup-reminder"
      className="border border-[var(--ark-accent)] bg-[var(--ark-bone)] px-5 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap"
    >
      <div className="inline-flex items-center gap-2 text-sm">
        <Database size={14} className="text-[var(--ark-accent)]" />
        <span>{msg}.</span>
        <span className="text-[var(--ark-muted)] text-xs">
          Save a copy in case you redeploy.
        </span>
      </div>
      <a
        href="#"
        data-testid="backup-reminder-cta"
        onClick={(e) => {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent("ark-tab", { detail: "backup" }));
        }}
        className="text-[0.62rem] uppercase tracking-[0.18em] text-[var(--ark-accent)] hover:text-black"
      >
        Backup now →
      </a>
    </div>
  );
}

function SavedDesigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/saved-designs")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this saved design?")) return;
    try {
      await api.delete(`/admin/saved-designs/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="saved-designs-tab">
      <h3 className="font-display text-2xl mb-6">
        Saved designs ({items.length})
      </h3>
      {loading ? (
        <div className="text-sm text-[var(--ark-muted)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-6 text-center">
          No saved designs yet — these are leads who clicked &ldquo;Save This Design&rdquo;.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <div
              key={d.id}
              data-testid={`saved-design-${d.id}`}
              className="border border-[var(--ark-border)] p-4 bg-white"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {d.furniture_code && (
                      <span className="font-mono text-[0.6rem] tracking-[0.18em] text-white bg-black px-2 py-0.5">
                        {d.furniture_code}
                      </span>
                    )}
                    <div className="font-display text-xl">{d.furniture_title}</div>
                  </div>
                  <div className="text-xs text-[var(--ark-muted)] mt-1">
                    {new Date(d.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => remove(d.id)}
                  className="p-2 hover:text-red-700"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="ark-label">Customer</div>
                  <div>{d.customer_name}</div>
                  <div>
                    <a
                      href={`mailto:${d.customer_email}`}
                      className="hover:text-[var(--ark-accent)]"
                    >
                      {d.customer_email}
                    </a>
                  </div>
                </div>
                <div>
                  <div className="ark-label">Finish</div>
                  <div>
                    {d.finish_type === "original"
                      ? "Keep original"
                      : `${d.finish_name || "—"} (${d.finish_type || "—"})`}
                  </div>
                  <div>{d.hardware || "—"}</div>
                </div>
                <div>
                  <div className="ark-label">Estimated price</div>
                  <div>
                    {d.estimated_price != null
                      ? `$${Number(d.estimated_price).toLocaleString("en-US", { maximumFractionDigits: 0 })} + HST`
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Coupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/coupons")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const toggleUsed = async (c) => {
    try {
      await api.patch(
        `/admin/coupons/${c.id}?used=${(!c.used).toString()}`,
      );
      load();
    } catch {
      toast.error("Couldn't update coupon");
    }
  };

  const usedCount = items.filter((c) => c.used).length;

  return (
    <div data-testid="coupons-tab">
      <h3 className="font-display text-2xl mb-2">
        Referral coupons ({items.length})
      </h3>
      <p className="text-xs text-[var(--ark-muted)] mb-6 uppercase tracking-[0.16em]">
        {usedCount} redeemed · {items.length - usedCount} outstanding
      </p>
      {loading ? (
        <div className="text-sm text-[var(--ark-muted)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-6 text-center">
          No coupons issued yet.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const expired = new Date(c.expires_at) < new Date();
            return (
              <div
                key={c.id}
                data-testid={`coupon-${c.id}`}
                className="flex items-center gap-3 border border-[var(--ark-border)] bg-white p-3 flex-wrap"
              >
                <span className="font-mono text-sm tracking-[0.16em] text-white bg-black px-2.5 py-1">
                  {c.code}
                </span>
                <span className="text-sm flex-1 min-w-[180px]">{c.email}</span>
                <span className="text-xs text-[var(--ark-muted)] uppercase tracking-[0.14em]">
                  {c.discount_pct}% off · expires {c.expires_at?.slice(0, 10)}
                  {expired && " · EXPIRED"}
                </span>
                <button
                  type="button"
                  onClick={() => toggleUsed(c)}
                  className={`text-[0.62rem] uppercase tracking-[0.18em] px-2.5 py-1.5 border transition-colors ${
                    c.used
                      ? "border-black bg-black text-white"
                      : "border-[var(--ark-border)] text-[var(--ark-muted)] hover:border-black hover:text-black"
                  }`}
                >
                  {c.used ? "Redeemed" : "Mark redeemed"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Transformations() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const load = () =>
    api.get("/transformations").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const upload = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imageUrl = upload.data?.url;
      if (!imageUrl) throw new Error("Upload failed");
      // Sort order = next slot at the end (existing.length)
      await api.post("/admin/transformations", {
        image_url: imageUrl,
        label: newLabel.trim() || null,
        sort_order: items.length,
      });
      toast.success("Transformation added");
      setNewLabel("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateLabel = async (t, nextLabel) => {
    try {
      await api.patch(`/admin/transformations/${t.id}`, { label: nextLabel });
      load();
    } catch {
      toast.error("Couldn't update label");
    }
  };

  const move = async (t, direction) => {
    const idx = items.findIndex((x) => x.id === t.id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const other = items[targetIdx];
    try {
      await Promise.all([
        api.patch(`/admin/transformations/${t.id}`, {
          sort_order: other.sort_order,
        }),
        api.patch(`/admin/transformations/${other.id}`, {
          sort_order: t.sort_order,
        }),
      ]);
      load();
    } catch {
      toast.error("Couldn't reorder");
    }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this transformation photo?")) return;
    try {
      await api.delete(`/admin/transformations/${t.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="transformations-tab" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <h3 className="font-display text-2xl mb-6">Add a transformation</h3>
        <div className="border border-[var(--ark-border)] p-6 bg-white space-y-4">
          <div>
            <label className="ark-label">Optional label (e.g. Dining Table, Dresser)</label>
            <input
              data-testid="transformation-label-input"
              className="ark-input"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Dining Table"
            />
          </div>
          <label
            data-testid="transformation-upload-label"
            className={`block border border-dashed border-[var(--ark-border)] p-6 text-center cursor-pointer hover:border-[var(--ark-accent)] hover:text-[var(--ark-accent)] transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
          >
            <Upload size={16} className="inline mr-2" />
            <span className="text-[0.7rem] uppercase tracking-[0.18em]">
              {uploading ? "Uploading…" : "Choose a finished photo"}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="transformation-upload-input"
            />
          </label>
          <p className="text-xs text-[var(--ark-muted)]">
            Tip: use bright, well-lit photos of finished pieces. Landscape or
            tall portrait both work — they crop to a 4×5 frame on the homepage.
          </p>
        </div>
      </div>

      <div className="lg:col-span-7">
        <h3 className="font-display text-2xl mb-6">
          Gallery ({items.length})
        </h3>
        {items.length === 0 ? (
          <div className="text-sm text-[var(--ark-muted)] border border-dashed border-[var(--ark-border)] p-6 text-center">
            No transformations yet — add the first one on the left.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((t, i) => (
              <div
                key={t.id}
                data-testid={`transformation-row-${t.id}`}
                className="flex items-center gap-4 border border-[var(--ark-border)] p-3 bg-white"
              >
                <img
                  src={resolveImage(t.image_url)}
                  alt=""
                  className="h-20 w-20 object-cover flex-shrink-0 rounded-lg"
                />
                <input
                  className="ark-input flex-1"
                  defaultValue={t.label || ""}
                  placeholder="No label"
                  onBlur={(e) => {
                    if (e.target.value !== (t.label || "")) {
                      updateLabel(t, e.target.value.trim() || null);
                    }
                  }}
                />
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => move(t, -1)}
                    disabled={i === 0}
                    className="p-1 text-[var(--ark-muted)] hover:text-black disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(t, 1)}
                    disabled={i === items.length - 1}
                    className="p-1 text-[var(--ark-muted)] hover:text-black disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => remove(t)}
                  className="p-2 hover:text-red-700"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Backup() {
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const [mode, setMode] = useState("merge");
  const [status, setStatus] = useState(null);

  const loadStatus = () =>
    api.get("/admin/backup-status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    loadStatus();
  }, []);

  const lastBackupText = () => {
    if (!status) return "—";
    if (!status.last_at) return "Never";
    const d = status.days_since ?? 0;
    if (d === 0) return "Today";
    if (d === 1) return "Yesterday";
    return `${d} days ago`;
  };

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `ark-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const cnt = (data?.pieces || []).length;
      toast.success(`Backed up ${cnt} piece${cnt === 1 ? "" : "s"}`);
      loadStatus();
      window.dispatchEvent(new Event("ark-backup-saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup failed");
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (mode === "replace") {
      const ok = window.confirm(
        "REPLACE mode will delete every piece currently in the database before importing. Are you sure?",
      );
      if (!ok) return;
    }
    setImporting(true);
    setReport(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const payload = {
        pieces: parsed.pieces || [],
        palette: parsed.palette || null,
        content: parsed.content || null,
        mode,
      };
      const { data } = await api.post("/admin/restore", payload);
      setReport(data);
      toast.success(
        `Imported ${data.pieces_inserted} piece${data.pieces_inserted === 1 ? "" : "s"}` +
          (data.pieces_skipped ? `, skipped ${data.pieces_skipped} duplicate${data.pieces_skipped === 1 ? "" : "s"}` : ""),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Import failed — check the file is a valid ARK backup",
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="backup-page" className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* DOWNLOAD */}
      <div className="border border-[var(--ark-border)] p-7 bg-white">
        <div className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.18em] text-[var(--ark-accent)]">
          <Download size={11} /> Download
        </div>
        <h3 className="font-display text-2xl mt-2">Save a backup</h3>
        <p className="text-sm text-[var(--ark-muted)] mt-2 leading-relaxed">
          Downloads a single <span className="font-mono text-xs">.json</span> file
          with every piece (codes, photos, dimensions, sold status), your palette,
          and the home-page content. Photos themselves stay in object storage —
          this file is the index that points to them.
        </p>
        <button
          type="button"
          onClick={downloadBackup}
          disabled={busy}
          data-testid="backup-download-btn"
          className="ark-btn mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? "Preparing…" : "Download backup file"}
        </button>
        <div
          data-testid="backup-last"
          className="mt-4 text-[0.7rem] uppercase tracking-[0.16em] text-[var(--ark-muted)]"
        >
          Last backup ·{" "}
          <span
            className={
              status?.overdue ? "text-[var(--ark-accent)]" : "text-black"
            }
          >
            {lastBackupText()}
          </span>
        </div>
        <p className="text-xs text-[var(--ark-muted)] mt-3">
          Tip: keep a fresh copy on your computer before you redeploy or migrate
          the site.
        </p>
      </div>

      {/* IMPORT */}
      <div className="border border-[var(--ark-border)] p-7 bg-white">
        <div className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.18em] text-[var(--ark-accent)]">
          <Database size={11} /> Restore
        </div>
        <h3 className="font-display text-2xl mt-2">Import from a backup</h3>
        <p className="text-sm text-[var(--ark-muted)] mt-2 leading-relaxed">
          Use this on a fresh deployment to repopulate inventory from a backup
          file you downloaded earlier.
        </p>

        <div className="mt-5 space-y-2">
          <label className="ark-label">Import mode</label>
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="restore-mode"
                value="merge"
                checked={mode === "merge"}
                onChange={() => setMode("merge")}
                data-testid="restore-mode-merge"
                className="mt-1 accent-[var(--ark-accent)]"
              />
              <span className="text-sm">
                <strong>Merge</strong> — only adds pieces whose ID isn&apos;t
                already in the database. Safe default.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="restore-mode"
                value="replace"
                checked={mode === "replace"}
                onChange={() => setMode("replace")}
                data-testid="restore-mode-replace"
                className="mt-1 accent-[var(--ark-accent)]"
              />
              <span className="text-sm">
                <strong>Replace</strong> — wipes every piece, then imports.
                Careful: destructive.
              </span>
            </label>
          </div>
        </div>

        <label
          data-testid="restore-file-label"
          className={`mt-6 block border border-dashed border-[var(--ark-border)] p-6 text-center cursor-pointer hover:border-[var(--ark-accent)] hover:text-[var(--ark-accent)] transition-colors ${importing ? "opacity-60 pointer-events-none" : ""}`}
        >
          <span className="text-[0.7rem] uppercase tracking-[0.18em]">
            {importing ? "Importing…" : "Choose .json file to restore"}
          </span>
          <input
            type="file"
            accept="application/json,.json"
            onChange={handleFile}
            className="hidden"
            data-testid="restore-file-input"
          />
        </label>

        {report && (
          <div
            data-testid="restore-report"
            className="mt-5 border border-[var(--ark-border)] bg-[var(--ark-bone)] p-4 text-sm"
          >
            <div>
              <strong>{report.pieces_inserted}</strong> piece(s) inserted
              {report.pieces_skipped > 0 && (
                <>
                  , <strong>{report.pieces_skipped}</strong> duplicate(s) skipped
                </>
              )}
            </div>
            <div className="text-[var(--ark-muted)] text-xs mt-1">
              Palette: {report.palette_restored ? "restored" : "not in backup"} ·
              Content: {report.content_restored ? "restored" : "not in backup"} ·
              Mode: {report.mode}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

